/**
 * Shared API client — base fetch wrapper with auth token injection and error handling.
 * All domain API modules import `fetchApi` and helpers from here.
 */
import { apiBaseUrl } from '../apiBaseUrl';
import { isUuid } from '@overlayqa/shared';
import { log } from '@/lib/logger';
import { capture } from '@/lib/analytics';
import { toFriendlyNetworkError } from './networkError';
import { NETWORK_FAILURE_EVENT, buildNetworkFailureProps } from './networkFailure';
import { maybeNotifySessionExpired } from './sessionExpired';
import { tokenSessionForRead, refreshReadToken } from './readTokenRecovery';
import { isHandledResponse } from './errorLogLevel';
import { parseServerError } from './serverErrorMessage';
import { TimeoutError, startRequestTimeout } from './requestTimeout';
import { isPageUnloading, getPageExitCount, PageUnloadingError } from './pageUnloading';
import { classifyUpgradeBlock, type UpgradeBlock, type UpgradeReason } from './upgradeBlock';

export { isUuid };
export { classifyUpgradeBlock };
export type { UpgradeBlock, UpgradeReason };

// Single source: the import.meta.env read is isolated in lib/apiBaseUrl.ts so a
// consumer can stub it under ts-jest. Duplicating the default here let this
// module and apiBaseUrl() drift apart, which matters now that the extension
// auth bridge compares api origins across packages (2026-08-28).
const API_URL = apiBaseUrl();
const PROD_ORIGIN = 'https://api.overlayqa.com';

/**
 * Rewrite absolute production asset URLs to the local dev server so
 * DB-stored screenshots render during development.
 * No-op in production (API_URL matches the stored origin).
 */
export function resolveAssetUrl(url: string | null | undefined): string {
  if (!url) return '';
  if (API_URL === PROD_ORIGIN) return url;
  if (url.startsWith(PROD_ORIGIN)) {
    return url.replace(PROD_ORIGIN, API_URL);
  }
  return url;
}

log.info('[api/client] API client module loaded, API_URL:', { API_URL });

export type FetchOptions = RequestInit & {
  token?: string;
  userId?: string;
  /**
   * Opt-in upper bound on this request, covering the response body as well as
   * the headers. Omitted (the default) leaves the request unbounded, exactly as
   * every call behaved before this option existed.
   *
   * Deliberately per-call rather than a global default: one ceiling cannot be
   * right for both an AI analysis call and a POST that does three indexed
   * queries, so adoption is caller-by-caller starting with requests that gate a
   * whole screen. A bounded request that expires rejects with `TimeoutError`
   * (actionable copy, and `shouldRetryApiQuery` will not silently double it).
   * See requestTimeout.ts for the 2026-08-12 incident this comes from.
   */
  timeoutMs?: number;
  /**
   * Statuses this caller owns and surfaces itself. A match logs at warn instead
   * of error, so a handled refusal (e.g. the share-create 400 "cannot share with
   * yourself") does not reach console.error and get filed as a fake $exception.
   * Scoped per-call on purpose: every other request keeps that status loud.
   */
  expectedErrorStatuses?: readonly number[];
};

/** Server responses are wrapped in a `{ data, ...meta }` envelope. */
export type ApiEnvelope<T> = { data: T } & Record<string, unknown>;

/**
 * Shared fetch wrapper that returns the FULL server envelope — `data` plus any
 * sibling metadata the route returns (pagination `meta`, or the
 * `modeSwitched`/`switchReason` signal emitted when a live review is
 * force-switched to a snapshot). Most callers want `fetchApi`, which unwraps
 * just `data`; reach for this only when the metadata matters.
 */
export async function fetchApiEnvelope<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<ApiEnvelope<T>> {
  // A frozen request can reject only after Back has restored the page.
  // Bind it to the visit that started it, before pageshow clears unloading.
  const observedExitCount = getPageExitCount();
  const { token, userId, timeoutMs, expectedErrorStatuses, ...fetchOptions } = options;
  const session = token && (options.method ?? 'GET').toUpperCase() === 'GET' ? tokenSessionForRead(token) : null;

  // Shared commentsApi supplies Idempotency-Key for a lost-response retry.
  // Preserve caller headers here, where every dashboard request is sent.
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  if (userId) {
    headers.set('X-User-Id', userId);
  }

  log.info('[api/client] fetchApi', { method: options.method || 'GET', endpoint });

  // Opt-in deadline (null unless the caller asked for one), composed with the
  // caller's own signal so an existing cancellation is never overwritten.
  const timeout = startRequestTimeout(timeoutMs, fetchOptions.signal);

  /**
   * Turn OUR expired deadline into a reportable failure.
   *
   * A pending request emits nothing, which is why the 2026-08-12 incident was
   * invisible in PostHog: zero api_network_failure, zero $exception, and a user
   * who simply left. A bounded one lands here and becomes queryable, with
   * `timed_out` separating it from an offline/DNS rejection so the next
   * adoption candidate can be picked from data rather than guessed at.
   */
  const reportTimeout = (): TimeoutError => {
    const failure = new TimeoutError(timeout!.timeoutMs);
    log.warn('[api/client] Request deadline exceeded', { endpoint, timeoutMs });
    capture(NETWORK_FAILURE_EVENT, buildNetworkFailureProps(endpoint, failure));
    return failure;
  };

  try {
    const read = async () => {
      let response: Response;
      try {
        response = await fetch(`${API_URL}/api${endpoint}`, {
          ...fetchOptions,
          headers: new Headers(headers),
          ...(timeout ? { signal: timeout.signal } : {}),
        });
      } catch (err) {
        // Our own deadline, not a network failure: report it as what it is rather
        // than as a bare AbortError, which toFriendlyNetworkError passes through
        // untouched and which would show the browser's cancellation jargon.
        if (timeout?.didTimeout()) throw reportTimeout();

        // The caller owns this cancellation (FetchOptions.signal is passed to
        // fetch above, or composed by startRequestTimeout). It is not evidence
        // of a broken connection. Keep our own deadline report above this guard.
        if (fetchOptions.signal?.aborted) throw err;

        // We navigated away, so the browser cancelled this request. It rejects
        // with the same TypeError a dead network does, but it is not a failure
        // and nobody is left to see it. Reporting it filed four phantom
        // `api_network_failure` events and two `$exception`s for one user
        // finishing onboarding on 2026-08-20. See pageUnloading.ts.
        //
        // Checked AFTER the deadline branch on purpose: our own timeout firing
        // is a genuine failure even mid-navigation and keeps its reporting.
        // Chrome rejects pending reads just before pagehide during navigation.
        // Finish that task before classifying the failure; a cancelled
        // beforeunload or a tab switch leaves ordinary reporting intact.
        await new Promise<void>(resolve => setTimeout(resolve, 0));
        if (isPageUnloading()) throw new PageUnloadingError();
        if (observedExitCount !== getPageExitCount()) throw new PageUnloadingError();

        // Network-level failure — the request never completed. Forms render
        // error.message verbatim, so replace the browser's "Failed to fetch"
        // jargon with actionable copy (see networkError.ts).
        //
        // log.warn, NOT log.error: log.error reaches console.error in prod and
        // PostHog's capture_exceptions turns that into a $exception. This path is
        // usually a blip TanStack retries away (laptop wake fires `online` before
        // the network can carry a request). Incident 2026-07-09 filed three
        // exceptions for a failure the user never saw. The failure stays queryable
        // as `api_network_failure`; main.tsx raises the exception only once every
        // retry is exhausted and the user actually hits it.
        log.warn('[api/client] Network-level fetch failure', { endpoint, error: err });
        capture(NETWORK_FAILURE_EVENT, buildNetworkFailureProps(endpoint, err));
        throw toFriendlyNetworkError(err);
      }

      // Inside the deadline on purpose: fetch() resolves as soon as the HEADERS
      // arrive, so a server that then stalls the body hangs here just as hard as
      // it would have hung above. The signal is what unblocks the read, which is
      // why cleanup() lives in the finally below and not here.
      let data: unknown;
      try {
        data = await response.json();
      } catch (err) {
        if (timeout?.didTimeout()) throw reportTimeout();
        // Anything else (a malformed body) rethrows exactly as it did before this
        // option existed.
        throw err;
      }

      return { response, data };
    };

    let { response, data } = await read();
    if (response.status === 401 && session) {
      let refreshedToken: string | null;
      try {
        refreshedToken = await refreshReadToken(session, timeout?.signal ?? fetchOptions.signal);
      } catch (error) {
        if (timeout?.didTimeout()) throw reportTimeout();
        throw error;
      }
      if (refreshedToken) {
        headers.set('Authorization', `Bearer ${refreshedToken}`);
        // Exactly one replay, for reads only, inside the ORIGINAL deadline.
        // A second 401 follows the existing signup-safe expiry handling below.
        ({ response, data } = await read());
      }
    }

    return handleEnvelopeResponse<T>(endpoint, response, data, expectedErrorStatuses);
  } finally {
    timeout?.cleanup();
  }
}

/**
 * Status handling for a response whose body has already been read.
 *
 * Extracted from fetchApiEnvelope when the request deadline was added, so the
 * transport half (headers, deadline, body read) and the status half stay
 * readable instead of nesting four deep inside a try/finally. Behaviour is
 * unchanged: same log levels, same side effects, same ApiError.
 */
function handleEnvelopeResponse<T>(
  endpoint: string,
  response: Response,
  data: unknown,
  expectedErrorStatuses?: readonly number[],
): ApiEnvelope<T> {
  if (!response.ok) {
    const { code, message } = parseServerError(data);
    // 401 (session-expiry / signup bootstrap race), 403 (plan/trial upgrade
    // modal), 404 (caller recovery), and 429 (explicit retry/error state) are
    // EXPECTED, handled outcomes — the side effects below (and callers') deal
    // with them. A caller can also opt a status IN per-request via
    // expectedErrorStatuses when it owns and surfaces that refusal itself (e.g.
    // the share-create 400 "cannot share with yourself"). log.error reaches
    // console.error in prod, which PostHog capture_console_errors files as a
    // $exception, so a handled outcome shows up as a fake "crash" in Error
    // Tracking (incident 2026-07-13: a user who deleted their AURA demo
    // re-mounted the dead /issues tab and filed 2-3 handled-404 $exceptions per
    // visit for 3 days). Warn keeps DEBUG_OVERLAYQA console visibility without
    // the $exception. Unexpected statuses (5xx, un-opted 4xx) stay at log.error,
    // prod-visible from a screenshot (investigation 2026-07-07). Both branches
    // still throw ApiError below, unchanged.
    const logContext = { status: response.status, code, message, endpoint };
    if (isHandledResponse(response.status, expectedErrorStatuses)) {
      log.warn('[api/client] fetchApi error', logContext);
    } else {
      log.error('[api/client] fetchApi error', logContext);
    }

    // Surface plan/trial gates (403) as an in-context upgrade modal. Best-effort
    // side effect — fetchApi still throws so callers' own error handling runs.
    if (response.status === 403) {
      const block = classifyUpgradeBlock(code, message, data);
      if (block) upgradeInterceptor?.(block);
    }

    // Session-expiry broadcast (401 with no live Clerk session). Best-effort
    // side effect: the App listener redirects to /sign-in?expired=1, while
    // this still throws so callers' own error handling runs unchanged. A 401
    // WITH a live Clerk session (signup bootstrap-window race) does nothing.
    maybeNotifySessionExpired(response.status);

    throw new ApiError(response.status, message, code, data);
  }

  return data as ApiEnvelope<T>;
}

/**
 * Shared fetch wrapper — injects auth headers and unwraps the `{ data }`
 * envelope to return just the payload. Delegates to fetchApiEnvelope.
 */
export async function fetchApi<T>(
  endpoint: string,
  options: FetchOptions = {}
): Promise<T> {
  const envelope = await fetchApiEnvelope<T>(endpoint, options);
  return envelope.data;
}

import { ApiError } from './apiError';
export { ApiError };

// ─── Plan/trial gate handling ────────────────────────────────────────────────

/**
 * The app registers a handler here (see UpgradeModal) so a request blocked by
 * a plan/trial gate surfaces an upgrade modal instead of a silent error.
 */
let upgradeInterceptor: ((block: UpgradeBlock) => void) | null = null;

export function setUpgradeInterceptor(fn: ((block: UpgradeBlock) => void) | null): void {
  upgradeInterceptor = fn;
}

/**
 * Build a query-string from a flat params object, omitting undefined/null values.
 * Returns the "?key=val&..." prefix or empty string when there are no params.
 */
export function buildQuery(params: Record<string, string | undefined | null>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== '') {
      searchParams.set(key, value);
    }
  }
  const query = searchParams.toString();
  return query ? `?${query}` : '';
}
